import { EquipmentTypeBadge, useIsRetiredType } from '@/components/badges/EquipmentTypeBadge';
import { RecallTopicBadge } from '@/components/badges/RecallTopicBadge';
import { BrandChip, BrandReviewStatusIcon } from '@/components/BrandChip';
import { DetailItem, FieldLabel } from '@/components/DetailItem';
import { EquipmentTypeSelect } from '@/components/EquipmentTypeSelect';
import { ImageThumbnail } from '@/components/ImageThumbnail';
import { useImageViewer } from '@/components/ImageViewer';
import { LinkedText } from '@/components/LinkedText';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
    allBrandsState,
    allEquipmentTypesState,
    brandAliasLookupState,
    currentUserIdState,
    equipmentTypeFamiliesState,
    joinAliasKeys,
    recallTopicsState,
    sharedNameplateTableState,
} from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { RecallChangedError } from '@rock/dao';
import { RecallDao } from '@rock/db-admin';
import {
    acceptRule,
    addCheck,
    addEveryModelRule,
    addModelRule,
    type CheckEdit,
    describeModelRule,
    identifierInNotice,
    identifierStrength,
    normalizeProductKind,
    planClassificationEdit,
    type ClassificationEditPlan,
    planRulingLift,
    planTopicRuleOut,
    planTopicRulingLift,
    planTopicsEdit,
    planTypeRuleOut,
    RECALL_TOPIC_FOLLOW_MAX_YEARS,
    type TopicsEditPlan,
    recallCatalogTypes,
    removeCheck,
    replaceCheck,
    suppressedInNotice,
    suppressRule,
    verifyRecallRules,
} from '@rock/recalls';
import type {
    Brand,
    BrandId,
    EquipmentTypeFamily,
    EquipmentTypeId,
    MergedRecallModelRule,
    MergedRecallRules,
    Recall,
    RecallClassification,
    RecallImage,
    RecallMatchStrength,
    RecallRuleSource,
    RecallTopic,
    RecallTopicId,
    Timestamp,
    UserId,
} from '@rock/types';
import { UserIdSchema } from '@rock/types';
import { type FamilyReach, familyOf, familyReach, normalizeBrandName, STRONG_IDENTIFIER_MIN_LENGTH } from '@rock/util';
import { useAtomValue } from 'jotai';
import { Check, Info, Pencil, Plus, X } from 'lucide-react';
import { type ComponentProps, type FC, type ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { StrengthLine } from '@/components/matching/matchingTags';
import { type MatchTier, TierBadge, TierCard } from '@/components/matching/TierCard';
import { FamilyLine } from '@/app/equipment/TypeFamilyLine';
import { NoticeFields, NoticeLegend } from './NoticeFields';
import { classifyRecallBrandNames, type RecallBrandName, recallBrandNameKeys } from './recallBrands';
import { CHECK_KIND_PHRASES, CheckForm, CheckRow } from './RecallCheckEditor';
import { RECALL_VERDICT_META } from './recallStatus';
import { AdminTag, CodeTag, LlmTag, RuleFacts, SourceTags, StrengthTag, Tag, TagRow } from './recallTags';

const log = logger('RecallRulesTab');
const recallDao = new RecallDao();

/** Below this the extractors' own confidence reads as a warning. */
const LOW_CONFIDENCE = 0.8;
const NOTICE_IMAGE_PX = 96;

/**
 * The notice on the left, as labelled fields with every identifier lit, then a legend and the source's other
 * fields; on the right the two quick checks, the FYI topics, the brands a model must carry and the types the
 * recall applies to, then the merged rules (their verbatim state as chip colour, who found them on hover). Each rule's
 * `verified` and the verification summary are recomputed here from the notice, so what shows is what the
 * text says, whatever was stored. Edits write the whole rules set back through the DAO (arrays are values),
 * re-verified, to `draftRules` when a re-process is pending, else to `rules`.
 */
export const RecallRulesTab: FC<{ recall: Recall }> = ({ recall }) => {
    const field: 'rules' | 'draftRules' = recall.draftRules ? 'draftRules' : 'rules';
    const stored = recall[field];
    const checked = useMemo(
        () => (stored ? verifyRecallRules(recall.noticeText, stored) : undefined),
        [recall.noticeText, stored],
    );
    const rules = checked?.rules;
    const verification = checked?.verification;
    const suppressed = useMemo(
        () => (rules ? suppressedInNotice(recall.noticeText, rules) : []),
        [recall.noticeText, rules],
    );
    const brands = useAsyncAtomValue(allBrandsState, []);
    const brandsById = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);
    const classification = recall.classification;
    const rawUserId = useAtomValue(currentUserIdState);
    const userId = useMemo(() => (rawUserId ? UserIdSchema.parse(rawUserId) : undefined), [rawUserId]);

    const write = async (label: string, work: () => Promise<void>, success: string): Promise<boolean> => {
        try {
            await work();
            toast.success(success);
            return true;
        } catch (err) {
            if (err instanceof RecallChangedError) {
                toast.error('This recall changed while you were editing', {
                    description: 'Check the latest version and try again.',
                });
                return false;
            }
            log.error(`Failed to ${label}`, { err, recallId: recall.id });
            toast.error(`Unable to ${label}`, {
                description: err instanceof Error ? err.message : 'Unexpected error.',
            });
            return false;
        }
    };
    const now = () => new Date().toISOString() as Timestamp;

    const writeRules = (next: MergedRecallRules, success: string) => {
        const { rules: verified, verification: summary } = verifyRecallRules(recall.noticeText, next);
        return write('update rules', () => recallDao.setRules(recall, field, verified, summary, now()), success);
    };
    const writeClassification = (plan: ClassificationEditPlan, success: string) =>
        write('update the classification', () => recallDao.setClassificationEdit(recall, plan, now()), success);
    const writeBrands = (brandIds: BrandId[]) =>
        write('update brands', () => recallDao.setBrandIds(recall, brandIds, now()), 'Brands updated');
    const writeGuidance = (guidance: string) =>
        write('update the guidance', () => recallDao.setAdditionalGuidance(recall, guidance, now()), 'Guidance saved');
    const returnToUnmatched = (reviewedBy: UserId) =>
        write(
            'return the recall to No Matched Type',
            () => recallDao.returnToUntypedBacklog(recall, reviewedBy, now()),
            'Returned to No Matched Type',
        );
    const canReturnToUnmatched = !!userId && recall.status === 'draft' && !recall.typed;
    const writeTopics = (plan: TopicsEditPlan, success: string) =>
        write('update topics', () => recallDao.setTopics(recall, plan, now()), success);

    return (
        <div className="grid items-start gap-4 lg:grid-cols-[1.2fr_1fr]">
            <NoticeCard
                recall={recall}
                rules={rules}
                harvested={verification?.harvestedNotExtracted ?? []}
                suppressed={suppressed}
            />

            <div className="flex flex-col gap-4">
                <TopicsCard classification={classification} onWrite={writeTopics} />
                <RequiredBrandsCard
                    recall={recall}
                    rules={rules}
                    brands={brands}
                    brandsById={brandsById}
                    onWrite={writeBrands}
                />
                <AppliesToCard
                    classification={classification}
                    onWrite={writeClassification}
                    onReturnToUnmatched={canReturnToUnmatched ? () => returnToUnmatched(userId) : undefined}
                />
                {rules ? (
                    <RulesCard
                        rules={rules}
                        draft={field === 'draftRules'}
                        noticeText={recall.noticeText}
                        harvested={verification?.harvestedNotExtracted ?? []}
                        typeIds={classification?.typeIds ?? []}
                        onWrite={writeRules}
                    />
                ) : (
                    <UnprocessedRulesCard />
                )}
                <GuidanceCard
                    key={`${recall.id}:${recall.additionalGuidance ?? ''}`}
                    guidance={recall.additionalGuidance}
                    onSave={writeGuidance}
                />
            </div>
        </div>
    );
};

const UnprocessedRulesCard: FC = () => (
    <Card>
        <CardHeader>
            <div className="flex items-center gap-2">
                <CardTitle>Rules</CardTitle>
                <RecallTierBadge tier={3} />
            </div>
            <CardDescription>Not processed yet. Process extracts matching rules from the notice.</CardDescription>
        </CardHeader>
    </Card>
);

const NoticeCard: FC<{
    recall: Recall;
    rules: MergedRecallRules | undefined;
    harvested: string[];
    suppressed: string[];
}> = ({ recall, rules, harvested, suppressed }) => (
    <Card>
        <CardHeader>
            <InfoTitle info="Exactly what the models see: these five fields, word for word.">Notice</InfoTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
            <NoticeFields noticeText={recall.noticeText} rules={rules} harvested={harvested} suppressed={suppressed} />
            <NoticeLegend />
            <div className="flex flex-col gap-2.5 border-t pt-3">
                <InfoTitle info="Not given to the models. Hazard, remedy, consumer contact, where it was sold and the images come from the recall source; where to find the identifiers is the extractors' note; the guidance is ours, edited beside the rules. All of them, and the notice's description, are shown to the homeowner, links tappable.">
                    Other Details
                </InfoTitle>
                {recall.additionalGuidance && (
                    <DetailItem
                        label="Additional guidance from Rock"
                        className="space-y-0"
                        valueClassName="font-normal"
                    >
                        <LinkedText text={recall.additionalGuidance} />
                    </DetailItem>
                )}
                {rules && (
                    <DetailItem
                        label="Where to find the identifiers"
                        className="space-y-0"
                        valueClassName="font-normal"
                    >
                        <LinkedText text={rules.whereToFindIdentifiers || '—'} />
                    </DetailItem>
                )}
                <DetailItem label="Hazard" className="space-y-0" valueClassName="font-normal">
                    <LinkedText text={recall.hazard ?? '—'} />
                </DetailItem>
                <DetailItem label="Remedy" className="space-y-0" valueClassName="font-normal">
                    <LinkedText text={recall.remedy ?? '—'} />
                </DetailItem>
                {recall.consumerContact && (
                    <DetailItem label="Consumer contact" className="space-y-0" valueClassName="font-normal">
                        <LinkedText text={recall.consumerContact} />
                    </DetailItem>
                )}
                {recall.soldAt && (
                    <DetailItem label="Sold at" className="space-y-0" valueClassName="font-normal">
                        <LinkedText text={recall.soldAt} />
                    </DetailItem>
                )}
                {!!recall.images?.length && (
                    <DetailItem label="Images" className="space-y-0" valueClassName="font-normal">
                        <NoticeImages images={recall.images} />
                    </DetailItem>
                )}
            </div>
        </CardContent>
    </Card>
);

const NoticeImages: FC<{ images: RecallImage[] }> = ({ images }) => {
    const { openImage, imageViewer } = useImageViewer();
    return (
        <div className="flex flex-wrap gap-3 pt-1">
            {images.map((image, index) => (
                <figure
                    key={`${index}:${image.url}`}
                    className="m-0 flex flex-col gap-1"
                    style={{ width: NOTICE_IMAGE_PX }}
                >
                    <ImageThumbnail
                        image={{ url: image.url, thumbnail: image.url, width: 0, height: 0 }}
                        alt={image.caption ?? 'Recall image'}
                        size={NOTICE_IMAGE_PX}
                        onClick={() => openImage({ url: image.url, alt: image.caption })}
                    />
                    {image.caption && (
                        <figcaption
                            className="line-clamp-3 text-[11px] leading-tight text-muted-foreground"
                            title={image.caption}
                        >
                            {image.caption}
                        </figcaption>
                    )}
                </figure>
            ))}
            {imageViewer}
        </div>
    );
};

/**
 * Rock's own guidance for the homeowner, shown beside the notice on the app's recall screen and in the
 * email: what the source leaves out, such as a manufacturer's exclusions or its inspection line. Keyed by
 * the recall and its saved value, so a save (or another admin's) resets the draft.
 */
const GuidanceCard: FC<{ guidance: string | undefined; onSave: (guidance: string) => Promise<boolean> }> = ({
    guidance,
    onSave,
}) => {
    const [draft, setDraft] = useState(guidance ?? '');
    const [saving, setSaving] = useState(false);
    const dirty = draft.trim() !== (guidance ?? '');
    // On success the card stays busy until the saved value arrives and remounts it (it is keyed by it).
    const save = async () => {
        setSaving(true);
        if (!(await onSave(draft))) setSaving(false);
    };
    return (
        <Card>
            <CardHeader>
                <CardTitle>Additional Guidance</CardTitle>
                <CardDescription>
                    Shown to the homeowner beside the notice, in the app and the email. Plain text: line breaks are
                    kept, and links, emails and phone numbers become tappable.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                <Textarea
                    rows={8}
                    value={draft}
                    placeholder="What the notice leaves out, such as the manufacturer's own exclusions or inspection line."
                    onChange={(event) => setDraft(event.target.value)}
                />
                <div className="flex gap-2">
                    <Button size="sm" onClick={() => void save()} disabled={!dirty || saving}>
                        Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDraft(guidance ?? '')} disabled={!dirty}>
                        Revert
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

const RulesCard: FC<{
    rules: MergedRecallRules;
    draft: boolean;
    noticeText: string;
    harvested: string[];
    typeIds: EquipmentTypeId[];
    onWrite: (next: MergedRecallRules, success: string) => Promise<boolean>;
}> = ({ rules, draft, noticeText, harvested, typeIds, onWrite }) => {
    const addIdentifier = (value: string) => {
        const next = addModelRule(rules, value, 'exact');
        if (next === rules) {
            toast.info(`${value} is already a rule`);
            return;
        }
        void onWrite(
            next,
            identifierInNotice(noticeText, value) ? `Added ${value}` : `Added ${value}, not printed in the notice`,
        );
    };
    const leftovers = [
        ...harvested.map((token) => ({ token, suppressed: false })),
        ...rules.suppressed.map((token) => ({ token, suppressed: true })),
    ];
    const noModelRules = rules.modelRules.length === 0;
    const strandedWeak =
        typeIds.length === 0
            ? rules.modelRules.filter((rule) => (rule.verified || rule.accepted) && identifierStrength(rule) === 'weak')
            : [];
    return (
        <Card>
            <CardHeader className="gap-3">
                <div className="flex items-center gap-2">
                    <CardTitle>Rules</CardTitle>
                    {draft && <Badge variant="outline">re-process draft</Badge>}
                    <ConfidenceBadge confidence={rules.confidence} />
                    <RecallTierBadge tier={3} />
                </div>
                <RulesDescription rules={rules} />
            </CardHeader>
            <CardContent className="flex flex-col gap-5 text-sm">
                <section className="flex flex-col gap-2">
                    <SectionLabel>Model numbers</SectionLabel>
                    <div className="flex flex-wrap items-center gap-2">
                        {rules.modelRules.map((rule, index) => (
                            <RuleChip
                                key={`${rule.kind}:${rule.value}`}
                                label={describeModelRule(rule)}
                                source={rule.source}
                                verified={rule.verified}
                                accepted={rule.accepted}
                                note={rule.note}
                                strength={identifierStrength(rule)}
                                onAccept={() =>
                                    onWrite(
                                        acceptRule(rules, 'modelRules', index),
                                        `Accepted ${describeModelRule(rule)}`,
                                    )
                                }
                                onSuppress={() =>
                                    onWrite(
                                        suppressRule(rules, 'modelRules', index),
                                        `Removed ${describeModelRule(rule)}`,
                                    )
                                }
                            />
                        ))}
                        {noModelRules && <span className="text-muted-foreground">No model rules.</span>}
                        <InlineAdd
                            label="Add a model number"
                            placeholder="Model number"
                            text={noModelRules ? 'Model' : undefined}
                            onAdd={addIdentifier}
                        />
                        {noModelRules && (
                            <TextAddButton
                                text="Match Any Model"
                                title="Match every model of this recall's brands, for a notice that names no model number. Only units of the types in Applies To match (a custom-typed unit is asked whether it is the product), and the checks still bind them."
                                onClick={() => onWrite(addEveryModelRule(rules), 'Added every model')}
                            />
                        )}
                    </div>
                    {strandedWeak.length > 0 && (
                        <p className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                            There are no types in Applies To, so{' '}
                            {joinWithAnd(strandedWeak.map((rule) => `“${describeModelRule(rule)}”`))} reaches only
                            custom-typed units, which are asked whether it’s their product: a weak rule matches only
                            units of the types in Applies To.
                        </p>
                    )}
                    {leftovers.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-muted-foreground">In the notice, not in the rules:</span>
                            {leftovers.map(({ token, suppressed }) => (
                                <button
                                    key={token}
                                    type="button"
                                    className={
                                        suppressed
                                            ? 'rounded bg-slate-200 px-1.5 py-0.5 font-mono text-xs text-slate-500 line-through decoration-slate-400 hover:bg-slate-300 hover:no-underline'
                                            : 'rounded bg-slate-200 px-1.5 py-0.5 font-mono text-xs text-slate-800 hover:bg-slate-300'
                                    }
                                    title={
                                        suppressed
                                            ? 'Removed by an admin. Click to add it back as an exact model rule'
                                            : 'Add as an exact model rule'
                                    }
                                    onClick={() => onWrite(addModelRule(rules, token, 'exact'), `Added ${token}`)}
                                >
                                    + {token}
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                <ChecksSection rules={rules} onWrite={onWrite} />

                {rules.serialRules.length > 0 && (
                    <section className="flex flex-col gap-2">
                        <SectionLabel
                            note={{
                                label: 'confirms a match',
                                hint: 'A unit whose serial an exact or prefix rule names is affected, when every check that binds it is a serial check. A range never names a serial, and a serial no rule names changes nothing: the homeowner reads the checks.',
                            }}
                        >
                            Serial numbers
                        </SectionLabel>
                        <div className="flex flex-wrap gap-2">
                            {rules.serialRules.map((rule, index) => (
                                <RuleChip
                                    key={`${rule.kind}:${rule.value}:${rule.appliesToModels.join(',')}`}
                                    label={`${rule.kind}: ${rule.value}${rule.appliesToModels.length ? ` (${rule.appliesToModels.join(', ')})` : ''}`}
                                    source={rule.source}
                                    verified={rule.verified}
                                    accepted={rule.accepted}
                                    note={rule.note}
                                    onAccept={() =>
                                        onWrite(acceptRule(rules, 'serialRules', index), 'Accepted serial rule')
                                    }
                                    onSuppress={() =>
                                        onWrite(suppressRule(rules, 'serialRules', index), 'Removed serial rule')
                                    }
                                />
                            ))}
                        </div>
                    </section>
                )}

                {rules.upcs.length > 0 && (
                    <section className="flex flex-col gap-1">
                        <SectionLabel
                            note={{
                                label: 'reference only',
                                hint: 'No unit stores a UPC, so these never match. Shown for reference.',
                            }}
                        >
                            UPCs
                        </SectionLabel>
                        <div className="font-mono text-xs">{rules.upcs.map((u) => u.value).join(', ')}</div>
                    </section>
                )}
            </CardContent>
        </Card>
    );
};

/**
 * What the homeowner checks on their unit, each in the notice's words, with Edit and Remove, and Add below.
 * An admin edit marks the checks admin-edited, so a re-process keeps them instead of the extractors'. No
 * checks means every unit of the listed models is affected.
 */
const ChecksSection: FC<{
    rules: MergedRecallRules;
    onWrite: (next: MergedRecallRules, success: string) => Promise<boolean>;
}> = ({ rules, onWrite }) => {
    const [editing, setEditing] = useState<number | 'new' | undefined>();
    const save = async (edit: CheckEdit) => {
        const next =
            editing === 'new' || editing === undefined ? addCheck(rules, edit) : replaceCheck(rules, editing, edit);
        if (await onWrite(next, 'Check saved')) setEditing(undefined);
    };
    return (
        <section className="flex flex-col gap-1.5">
            <SectionLabel
                note={
                    rules.checksAdminEdited
                        ? {
                              label: 'edited by admin',
                              hint: "A re-process keeps these checks instead of the extractors'.",
                          }
                        : undefined
                }
            >
                Homeowner checks
            </SectionLabel>
            {rules.checks.length === 0 && editing === undefined && (
                <span className="text-xs text-muted-foreground">
                    {rules.serialRules.length > 0
                        ? 'No checks beyond the serial numbers below, which the homeowner reads too.'
                        : 'No checks: every unit of the listed models is affected.'}
                </span>
            )}
            {rules.checks.map((check, index) =>
                editing === index ? (
                    <CheckForm
                        key={`${index}:${check.text}`}
                        initial={check}
                        modelRules={rules.modelRules}
                        onSave={save}
                        onCancel={() => setEditing(undefined)}
                    />
                ) : (
                    <CheckRow
                        key={`${index}:${check.text}`}
                        check={check}
                        modelRules={rules.modelRules}
                        locked={editing !== undefined}
                        onEdit={() => setEditing(index)}
                        onRemove={() => void onWrite(removeCheck(rules, index), 'Check removed')}
                    />
                ),
            )}
            {editing === 'new' ? (
                <CheckForm modelRules={rules.modelRules} onSave={save} onCancel={() => setEditing(undefined)} />
            ) : (
                <Button
                    size="sm"
                    variant="outline"
                    className="w-fit"
                    disabled={editing !== undefined}
                    onClick={() => setEditing('new')}
                >
                    <Plus className="h-3 w-3" /> Add check
                </Button>
            )}
        </section>
    );
};

/** A rules section's label; `note` is a neutral tag beside it, its hint on hover. */
const SectionLabel: FC<{ note?: SectionTag; children: ReactNode }> = ({ note, children }) => (
    <div className="flex flex-wrap items-center gap-1.5">
        <FieldLabel>{children}</FieldLabel>
        {note && <SectionTagBadge tag={note} className="text-muted-foreground" />}
    </div>
);

interface SectionTag {
    label: string;
    hint: string;
}

const SectionTagBadge: FC<{ tag: SectionTag; className: string }> = ({ tag, className }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Badge variant="outline" className={className}>
                {tag.label}
            </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{tag.hint}</TooltipContent>
    </Tooltip>
);

const ConfidenceBadge: FC<{ confidence: number }> = ({ confidence }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Badge
                variant="outline"
                className={cn(
                    confidence < LOW_CONFIDENCE
                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                        : 'text-muted-foreground',
                )}
            >
                {Math.round(confidence * 100)}% confidence
            </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
            The lower of the two extractors' own confidence. Matching never reads it; it is a hint for review.
        </TooltipContent>
    </Tooltip>
);

/** What the rules cover, in a sentence: the scope, then what a homeowner checks. */
const RulesDescription: FC<{ rules: MergedRecallRules }> = ({ rules }) => {
    const kinds = [
        ...new Set([
            ...rules.checks.map((check) => CHECK_KIND_PHRASES[check.kind]),
            ...(rules.serialRules.length > 0 ? [CHECK_KIND_PHRASES.serial] : []),
        ]),
    ];
    const usable = rules.modelRules.filter((rule) => rule.verified || rule.accepted);
    const appliesToNothing = usable.length === 0;
    const conditionsMissing = rules.scope === 'listed-models-with-conditions' && kinds.length === 0;
    const sentences = [
        scopeSentence(rules.modelRules, usable, kinds.length > 0),
        !appliesToNothing && kinds.length > 0 ? `Homeowner checks the ${joinWithAnd(kinds)}.` : undefined,
        conditionsMissing ? 'The extractors read conditions in the notice but stated none; add the checks.' : undefined,
        rules.scope === 'unclear' ? 'The extractors couldn’t tell the scope; check the notice.' : undefined,
        rules.scopeDiffers ? 'Extractors disagreed on the scope; the broader reading is kept.' : undefined,
    ].filter(Boolean);
    const caution = appliesToNothing || conditionsMissing || rules.scope === 'unclear';
    return <CardDescription className={caution ? 'text-amber-800' : undefined}>{sentences.join(' ')}</CardDescription>;
};

/** What the model rules reach: nothing yet, every model of the brands, or the listed ones, in whole or in part. */
function scopeSentence(all: MergedRecallModelRule[], usable: MergedRecallModelRule[], conditional: boolean): string {
    if (all.length === 0)
        return 'No model numbers listed, so it applies to nothing yet. Match Any Model if the notice names none.';
    if (usable.length === 0) return 'None of its model numbers is accepted yet, so it applies to nothing.';
    const units = conditional ? 'some units' : 'every unit';
    if (usable.some((rule) => rule.kind === 'any')) {
        return `Applies to ${units} of its brands among the types in Applies To, with or without a model number.`;
    }
    return conditional ? 'Applies to only some units of these models.' : 'Applies to every unit of these models.';
}

function joinWithAnd(items: string[]): string {
    return items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * One rule. Colour is its state: green printed in the notice, blue not printed but accepted by an admin (it
 * matches), amber not printed and not accepted (it does not); a dashed border means only one extractor
 * found it. Who found it, where it came from and how strongly it identifies a unit are on hover.
 */
const RuleChip: FC<{
    label: string;
    source: RecallRuleSource;
    verified: boolean;
    accepted?: boolean;
    note: string | null;
    strength?: RecallMatchStrength;
    onAccept: () => void;
    onSuppress: () => void;
}> = ({ label, source, verified, accepted, note, strength, onAccept, onSuppress }) => {
    const active = verified || accepted;
    const singleSource = source === 'sol' || source === 'sonnet';
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    className={cn(
                        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-xs',
                        verified
                            ? 'border-emerald-300 bg-emerald-50'
                            : accepted
                              ? 'border-sky-300 bg-sky-50'
                              : 'border-amber-300 bg-amber-50',
                        singleSource && 'border-dashed bg-background',
                    )}
                >
                    {label}
                    {!active && (
                        <button type="button" className="text-amber-800 underline" onClick={onAccept}>
                            accept
                        </button>
                    )}
                    <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={onSuppress}
                        aria-label={`Remove ${label}`}
                    >
                        <X className="h-3 w-3" />
                    </button>
                </span>
            </TooltipTrigger>
            <TooltipContent className="text-pretty">
                <RuleFacts source={source} verified={verified} accepted={accepted} note={note} strength={strength} />
            </TooltipContent>
        </Tooltip>
    );
};

/**
 * Broad categorization: the FYI topics the recall reaches homes through. A home following any of them gets the
 * recall as an FYI, quietly, beside whatever its rules match. A pill a single classifier chose is tinted; a
 * dashed one is a suggestion the latest reclassify made, to take up or rule out.
 */
const TopicsCard: FC<{
    classification: RecallClassification | undefined;
    onWrite: (plan: TopicsEditPlan, success: string) => Promise<boolean>;
}> = ({ classification, onWrite }) => {
    const topics = useAsyncAtomValue(recallTopicsState, []);
    const topicsById = useMemo(() => new Map(topics.map((topic) => [topic.id, topic])), [topics]);
    return (
        <Card className="gap-0 py-4">
            <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-1.5">
                    <CardTitle className="text-base">Topics</CardTitle>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground"
                                aria-label="About topics"
                            >
                                <Info className="h-3.5 w-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-pretty">
                            Topics put this recall in FYI recalls: once approved, every home following one of its topics
                            from before it was published gets it as an FYI, with no notification; a follow reaches back
                            up to {RECALL_TOPIC_FOLLOW_MAX_YEARS} years. A recall can have topics and types; its types
                            still match units by their model numbers.
                        </TooltipContent>
                    </Tooltip>
                    {classification?.topicsAdminEdited && <Badge variant="outline">admin edited</Badge>}
                </div>
                {classification ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {classification.topicIds.map((id) => (
                            <TopicChoiceChip
                                key={id}
                                topicId={id}
                                topic={topicsById.get(id)}
                                classification={classification}
                                onRemove={() =>
                                    onWrite(
                                        planTopicsEdit(
                                            classification,
                                            classification.topicIds.filter((t) => t !== id),
                                        ),
                                        'Topics updated',
                                    )
                                }
                            />
                        ))}
                        {classification.topicIds.length === 0 && (
                            <span className="text-sm text-muted-foreground">No topics</span>
                        )}
                        {classification.suggestedTopicIds?.map((id) => (
                            <SuggestionChip
                                key={`suggested:${id}`}
                                tone="topic"
                                label={topicsById.get(id)?.title ?? id}
                                onAccept={() =>
                                    onWrite(
                                        planTopicsEdit(classification, [...classification.topicIds, id]),
                                        'Topic added',
                                    )
                                }
                                onRuleOut={() =>
                                    onWrite(planTopicRuleOut(classification, id), 'Marked as not applying')
                                }
                            />
                        ))}
                        <TopicPicker
                            topics={topics}
                            exclude={classification.topicIds}
                            onPick={(id) =>
                                onWrite(planTopicsEdit(classification, [...classification.topicIds, id]), 'Topic added')
                            }
                        />
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground">Not classified yet. Reclassify to run it.</p>
                )}
                {!!classification?.ruledOutTopicIds?.length && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Not:</span>
                        {classification.ruledOutTopicIds.map((id) => (
                            <RuledOutTopicChip
                                key={id}
                                topicId={id}
                                title={topicsById.get(id)?.title}
                                onLift={() => void onWrite(planTopicRulingLift(classification, id), 'Ruling lifted')}
                            />
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const RuledOutTopicChip: FC<{ topicId: RecallTopicId; title: string | undefined; onLift: () => void }> = ({
    topicId,
    title,
    onLift,
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span className="inline-flex">
                <RecallTopicBadge
                    topicId={topicId}
                    title={title}
                    onRemove={onLift}
                    removeLabel={`Lift the ruling on ${title ?? topicId}`}
                    className="bg-transparent text-muted-foreground ring-1 ring-inset ring-border"
                    labelClassName="line-through"
                />
            </span>
        </TooltipTrigger>
        <TooltipContent className="text-pretty flex max-w-xs flex-col gap-1">
            <TagRow label="Ruled out by">
                <AdminTag />
            </TagRow>
            <span className="text-xs">No reclassify will file the recall under it. × lifts the ruling.</span>
        </TooltipContent>
    </Tooltip>
);

const SUGGESTION_TONES = {
    type: 'border-blue-300 bg-blue-50/50 text-blue-700',
    topic: 'border-violet-300 bg-violet-50/50 text-violet-700',
} as const;

/**
 * A type or topic the classifiers now propose that the recall does not carry (a draft, live or admin-edited
 * recall keeps its lists through a reclassify): dashed, in the list's own color, until an admin takes it up or
 * says it doesn't apply.
 */
const SuggestionChip: FC<{
    tone: keyof typeof SUGGESTION_TONES;
    label: string;
    onAccept: () => void;
    onRuleOut: () => void;
}> = ({ tone, label, onAccept, onRuleOut }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <span
                className={cn(
                    'inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs',
                    SUGGESTION_TONES[tone],
                )}
            >
                {label}
                <button
                    type="button"
                    className="text-emerald-600 hover:text-emerald-700"
                    aria-label={`Add ${label}`}
                    onClick={onAccept}
                >
                    <Check className="h-3 w-3" />
                </button>
                <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`${label} doesn't apply`}
                    onClick={onRuleOut}
                >
                    <X className="h-3 w-3" />
                </button>
            </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-pretty">
            Suggested by the latest reclassify. ✓ adds it; × says no, this doesn't apply, and no reclassify suggests it
            again.
        </TooltipContent>
    </Tooltip>
);

const TopicChoiceChip: FC<{
    topicId: RecallTopicId;
    topic: RecallTopic | undefined;
    classification: RecallClassification;
    onRemove: () => void;
}> = ({ topicId, topic, classification, onRemove }) => {
    const choosers = Object.entries(classification.byClassifier)
        .filter(([, result]) => result.topicIds.includes(topicId))
        .map(([llm]) => llm);
    const total = Object.keys(classification.byClassifier).length;
    const partial = choosers.length > 0 && choosers.length < total;
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="inline-flex">
                    <RecallTopicBadge
                        topicId={topicId}
                        title={topic?.title}
                        onRemove={onRemove}
                        className={
                            partial ? 'bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-200' : undefined
                        }
                    />
                </span>
            </TooltipTrigger>
            <TooltipContent className="text-pretty flex max-w-xs flex-col gap-1">
                {topic ? <span>{topic.description}</span> : <span>No topic has this id.</span>}
                {choosers.length === 0 ? (
                    <TagRow label="Added by">
                        <AdminTag />
                    </TagRow>
                ) : (
                    <TagRow label="Classified by">
                        {choosers.map((llm) => (
                            <LlmTag key={llm} llm={llm} short />
                        ))}
                    </TagRow>
                )}
            </TooltipContent>
        </Tooltip>
    );
};

/** The topics not on the recall yet, in list order, each with its description on hover. */
const TopicPicker: FC<{ topics: RecallTopic[]; exclude: RecallTopicId[]; onPick: (id: RecallTopicId) => void }> = ({
    topics,
    exclude,
    onPick,
}) => {
    const [open, setOpen] = useState(false);
    const options = topics.filter((topic) => !exclude.includes(topic.id));
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <IconAddButton label="Add a topic" />
            </PopoverTrigger>
            <PopoverContent className="w-[280px]">
                <div className="max-h-[320px] overflow-auto">
                    {options.map((topic) => (
                        <button
                            key={topic.id}
                            type="button"
                            title={topic.description}
                            className="flex w-full items-center rounded px-2 py-1 text-left text-sm hover:bg-accent"
                            onClick={() => {
                                setOpen(false);
                                onPick(topic.id);
                            }}
                        >
                            {topic.title}
                        </button>
                    ))}
                    {options.length === 0 && (
                        <div className="px-2 py-1 text-sm text-muted-foreground">
                            {topics.length ? 'Every topic is on it.' : 'No topics yet. Add them on the Topics tab.'}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

/**
 * The catalog types a homeowner would file this product under: one row of chips (lighter when only one
 * classifier chose the type; who chose it and its family on hover; dashed for a suggestion the latest
 * reclassify made), a plus to add one, the family types it also asks about, the types an admin ruled out, and
 * the classifier's product kind, which only groups the untyped backlog.
 */
const AppliesToCard: FC<{
    classification: RecallClassification | undefined;
    onWrite: (plan: ClassificationEditPlan, success: string) => Promise<boolean>;
    onReturnToUnmatched: (() => Promise<boolean>) | undefined;
}> = ({ classification, onWrite, onReturnToUnmatched }) => {
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    const typeTitles = useMemo(() => new Map(types.map((t) => [t.id, t.title])), [types]);
    const classifiable = useMemo(() => recallCatalogTypes(types), [types]);
    return (
        <CompactCard
            title="Applies To"
            tier={2}
            badge={classification?.adminEdited ? <Badge variant="outline">admin edited</Badge> : undefined}
        >
            {classification ? (
                <>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {classification.typeIds.map((id) => (
                            <TypeChoiceChip
                                key={id}
                                typeId={id}
                                title={typeTitles.get(id)}
                                family={familyOf(id, families)}
                                classification={classification}
                                onRemove={() =>
                                    onWrite(
                                        planClassificationEdit(classification, {
                                            typeIds: classification.typeIds.filter((t) => t !== id),
                                            productKind: classification.productKind,
                                        }),
                                        'Types updated',
                                    )
                                }
                            />
                        ))}
                        {classification.typeIds.length === 0 && (
                            <span className="text-sm text-muted-foreground">No types</span>
                        )}
                        {classification.suggestedTypeIds?.map((id) => (
                            <SuggestionChip
                                key={`suggested:${id}`}
                                tone="type"
                                label={typeTitles.get(id) ?? id}
                                onAccept={() =>
                                    onWrite(
                                        planClassificationEdit(classification, {
                                            typeIds: [...classification.typeIds, id],
                                            productKind: classification.productKind,
                                        }),
                                        'Type added',
                                    )
                                }
                                onRuleOut={() => onWrite(planTypeRuleOut(classification, id), 'Marked as not applying')}
                            />
                        ))}
                        <EquipmentTypeSelect
                            selected={classification.typeIds}
                            onPick={(ids) =>
                                onWrite(
                                    planClassificationEdit(classification, {
                                        typeIds: [...classification.typeIds, ...ids],
                                        productKind: classification.productKind,
                                    }),
                                    'Types updated',
                                )
                            }
                            types={classifiable}
                            trigger={<IconAddButton label="Add a type" />}
                        />
                    </div>
                    <FamilyReachLines
                        reach={familyReach(classification.typeIds, families)}
                        ruledOut={classification.ruledOutTypeIds ?? []}
                        typeTitles={typeTitles}
                    />
                    {!!classification.ruledOutTypeIds?.length && (
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-xs text-muted-foreground">Not:</span>
                            {classification.ruledOutTypeIds.map((id) => (
                                <RuledOutChip
                                    key={id}
                                    typeId={id}
                                    title={typeTitles.get(id)}
                                    onLift={() => void onWrite(planRulingLift(classification, id), 'Ruling lifted')}
                                />
                            ))}
                        </div>
                    )}
                    <ProductKind
                        kind={classification.productKind}
                        onSave={(productKind) =>
                            onWrite(
                                planClassificationEdit(classification, {
                                    typeIds: classification.typeIds,
                                    productKind,
                                }),
                                'Product kind updated',
                            )
                        }
                    />
                    {onReturnToUnmatched && (
                        <ReturnToUnmatched productKind={classification.productKind} onConfirm={onReturnToUnmatched} />
                    )}
                </>
            ) : (
                <p className="text-sm text-muted-foreground">Not classified yet. Reclassify to run it.</p>
            )}
        </CompactCard>
    );
};

const TypeChoiceChip: FC<{
    typeId: EquipmentTypeId;
    title: string | undefined;
    family: EquipmentTypeFamily | undefined;
    classification: RecallClassification;
    onRemove: () => void;
}> = ({ typeId, title, family, classification, onRemove }) => {
    const choosers = Object.entries(classification.byClassifier)
        .filter(([, result]) => result.typeIds.includes(typeId))
        .map(([llm]) => llm);
    const total = Object.keys(classification.byClassifier).length;
    const partial = choosers.length > 0 && choosers.length < total;
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="inline-flex">
                    <EquipmentTypeBadge
                        typeId={typeId}
                        onRemove={onRemove}
                        className={partial ? 'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-200' : undefined}
                    />
                </span>
            </TooltipTrigger>
            <TooltipContent className="text-pretty flex flex-col gap-1">
                {title && <span className="font-medium">{title}</span>}
                {family && <span>{family.name} family</span>}
                {choosers.length === 0 ? (
                    <TagRow label="Added by">
                        <AdminTag />
                    </TagRow>
                ) : (
                    <TagRow label="Classified by">
                        {choosers.map((llm) => (
                            <LlmTag key={llm} llm={llm} short />
                        ))}
                    </TagRow>
                )}
            </TooltipContent>
        </Tooltip>
    );
};

/**
 * The types a recall reaches beyond its chips: a unit filed under another type in a listed type's family
 * matches too, and its owner is asked the type (`typeAffinity` in `evaluateUnit`). A ruling steers only the
 * classifier, so a ruled-out sibling is still asked — and says so.
 */
const FamilyReachLines: FC<{
    reach: FamilyReach[];
    ruledOut: EquipmentTypeId[];
    typeTitles: Map<EquipmentTypeId, string>;
}> = ({ reach, ruledOut, typeTitles }) => (
    <>
        {reach.map(({ family, others }) => (
            <FamilyLine key={family.id} family={family} members={others} typeTitles={typeTitles} ruledOut={ruledOut} />
        ))}
    </>
);

const RuledOutChip: FC<{ typeId: EquipmentTypeId; title: string | undefined; onLift: () => void }> = ({
    typeId,
    title,
    onLift,
}) => {
    const retired = useIsRetiredType(typeId);
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="inline-flex">
                    <EquipmentTypeBadge
                        typeId={typeId}
                        onRemove={onLift}
                        removeLabel={`Lift the ruling on ${typeId}`}
                        className="bg-transparent text-muted-foreground ring-1 ring-inset ring-border"
                        idClassName="line-through"
                    />
                </span>
            </TooltipTrigger>
            <TooltipContent className="text-pretty flex flex-col gap-1">
                {title && <span className="font-medium">{title}</span>}
                <TagRow label="Ruled out by">
                    <AdminTag />
                </TagRow>
                <span className="text-xs">
                    {retired
                        ? 'Retired, so no reclassify offers it anyway. The ruling matters only if the type is restored. × lifts it.'
                        : 'No reclassify will type the recall with it. × lifts the ruling.'}
                </span>
            </TooltipContent>
        </Tooltip>
    );
};

const ProductKind: FC<{ kind: string; onSave: (kind: string) => Promise<boolean> }> = ({ kind, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(kind);
    const save = () => {
        setEditing(false);
        const next = normalizeProductKind(draft);
        if (next !== kind) void onSave(next);
    };
    return editing ? (
        <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
                event.preventDefault();
                save();
            }}
        >
            <span className="text-xs text-muted-foreground">kind</span>
            <Input
                className="h-7 w-[200px] font-mono text-xs"
                value={draft}
                autoFocus
                onChange={(event) => setDraft(event.target.value)}
                onBlur={save}
            />
        </form>
    ) : (
        <button
            type="button"
            className="group inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title="The classifier's product kind: groups the No Matched Type tab. Click to edit."
            onClick={() => {
                setDraft(kind);
                setEditing(true);
            }}
        >
            kind: <span className="font-mono">{kind}</span>
            <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100" />
        </button>
    );
};

const ReturnToUnmatched: FC<{ productKind: string; onConfirm: () => Promise<boolean> }> = ({
    productKind,
    onConfirm,
}) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button size="sm" variant="outline" className="w-fit" onClick={() => setOpen(true)}>
                Return to Unmatched…
            </Button>
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Return this recall to No Matched Type?</AlertDialogTitle>
                        <AlertDialogDescription>
                            It leaves Drafts and counts under “{productKind}” on the No Matched Type tab. Its rules
                            (with your accepts, removals and added identifiers), brands and impact preview are cleared,
                            so a later process pays to extract them again. The classifier gets the types and kind back:
                            a reclassify can type it again, but never with a ruled-out type.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void onConfirm()}>Return to Unmatched</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

/**
 * The brand gate: a model must carry one of these brands to match at all. Below it, the other names the
 * extractors read, as pills — one click requires a name the catalog already knows; the others link to
 * where the name is settled (its brand request, its shared nameplate, or the Brands search).
 */
const RequiredBrandsCard: FC<{
    recall: Recall;
    rules: MergedRecallRules | undefined;
    brands: Brand[];
    brandsById: Map<BrandId, Brand>;
    onWrite: (brandIds: BrandId[]) => Promise<boolean>;
}> = ({ recall, rules, brands, brandsById, onWrite }) => {
    // With a re-process under review the edit lands on the draft's gate (promoted by Approve), never on
    // the live one — see `BaseRecallDao.setBrandIds`.
    const draft = !!recall.draftRules;
    const gate = draft ? (recall.draftBrandIds ?? recall.brandIds) : recall.brandIds;
    // Every read the classification needs, with no fallbacks: nothing shows until the catalog, the table
    // and the projection have all loaded, since a pill that guesses, then corrects, misleads.
    const catalog = useAsyncAtomValue(allBrandsState);
    const sharedNameplates = useAsyncAtomValue(sharedNameplateTableState);
    const aliasKeys = useMemo(() => joinAliasKeys(recallBrandNameKeys(rules?.brands ?? [])), [rules]);
    const aliases = useAsyncAtomValue(brandAliasLookupState(aliasKeys));
    const names = useMemo(
        () =>
            catalog && sharedNameplates && aliases
                ? classifyRecallBrandNames(rules?.brands ?? [], catalog, gate, sharedNameplates, aliases)
                : [],
        [rules, catalog, gate, sharedNameplates, aliases],
    );
    const candidates = names.filter(
        (entry, index) =>
            entry.state !== 'gating' &&
            (!entry.brand || names.findIndex((other) => other.brand?.id === entry.brand?.id) === index),
    );
    return (
        <CompactCard
            title="Required Brand(s)"
            tier={1}
            badge={draft ? <Badge variant="outline">re-process draft</Badge> : undefined}
        >
            <div className="flex flex-wrap items-center gap-1.5">
                {gate.map((id) => {
                    const brand = brandsById.get(id);
                    const readAs = names.filter((entry) => entry.brand?.id === id);
                    return (
                        <Tooltip key={id}>
                            <TooltipTrigger asChild>
                                <span className="inline-flex">
                                    {brand ? (
                                        <BrandChip
                                            brand={brand}
                                            onRemove={() => onWrite(gate.filter((b) => b !== id))}
                                        />
                                    ) : (
                                        <Badge variant="outline" className="font-mono text-xs">
                                            {id}
                                        </Badge>
                                    )}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent className="text-pretty flex flex-col gap-1">
                                {readAs.length === 0 ? (
                                    <TagRow label="Added by">
                                        <AdminTag />
                                    </TagRow>
                                ) : (
                                    readAs.map((entry) => (
                                        <ReadAs key={`${entry.name}:${entry.source}`} entry={entry} />
                                    ))
                                )}
                            </TooltipContent>
                        </Tooltip>
                    );
                })}
                {gate.length === 0 && <span className="text-sm text-muted-foreground">None, so nothing matches</span>}
                <BrandPicker brands={brands} exclude={gate} onPick={(id) => onWrite([...gate, id])} />
            </div>
            {candidates.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Also named:</span>
                    {candidates.map((entry) => (
                        <BrandCandidatePill
                            key={`${entry.name}:${entry.source}`}
                            entry={entry}
                            onRequire={(id) => onWrite([...gate, id])}
                        />
                    ))}
                </div>
            )}
        </CompactCard>
    );
};

const BrandCandidatePill: FC<{ entry: RecallBrandName; onRequire: (brandId: BrandId) => void }> = ({
    entry,
    onRequire,
}) => {
    const brand = entry.brand;
    const pill =
        entry.state === 'resolved' && brand ? (
            <button
                type="button"
                className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-800 hover:bg-slate-200"
                onClick={() => onRequire(brand.id)}
            >
                + {brand.displayName ?? brand.canonicalName}
            </button>
        ) : (
            <Link
                to={candidateLink(entry)}
                className={cn(
                    'rounded-full border border-dashed px-2 py-0.5 text-xs hover:underline',
                    entry.state === 'requested'
                        ? 'border-amber-400 bg-amber-50 text-amber-900'
                        : 'border-slate-300 text-muted-foreground',
                )}
            >
                {entry.name}
            </Link>
        );
    return (
        <Tooltip>
            <TooltipTrigger asChild>{pill}</TooltipTrigger>
            <TooltipContent className="text-pretty flex max-w-xs flex-col gap-1">
                <ReadAs entry={entry} />
                {entry.state === 'resolved' ? (
                    <span>
                        In the catalog{entry.line ? `, read as its ${entry.line.displayName} line` : ''}, not required.
                        Click to require it.
                    </span>
                ) : entry.state === 'shared' ? (
                    <span>
                        Shared nameplate: {sideNames(entry)} print it, and a recall has no space to route by. Require
                        the right side above.
                    </span>
                ) : entry.state === 'requested' ? (
                    <span className="flex flex-wrap items-center gap-1">
                        Brand request pending {entry.requestId && <CodeTag>{entry.requestId}</CodeTag>}. Click to decide
                        what it is.
                    </span>
                ) : (
                    <span>No catalog match, too generic to request. Click to look in Brands.</span>
                )}
            </TooltipContent>
        </Tooltip>
    );
};

/** Where a name that is not a one-click require is settled. */
function candidateLink(entry: RecallBrandName): string {
    if (entry.state === 'requested' && entry.requestId) {
        return `/products/brand-requests/${encodeURIComponent(entry.requestId)}`;
    }
    if (entry.state === 'shared' && entry.shared) {
        return `/products/shared-nameplates/${encodeURIComponent(entry.shared.key)}`;
    }
    return `/products/brands?q=${encodeURIComponent(normalizeBrandName(entry.name).display)}`;
}

function sideNames(entry: RecallBrandName): string {
    const names = entry.shared?.sides.map((side) => side.canonicalName) ?? [];
    return names.length > 0 ? names.join(' and ') : 'several companies';
}

/** A brand name as the extractors read it, with who read it. */
const ReadAs: FC<{ entry: RecallBrandName }> = ({ entry }) => (
    <TagRow label={`“${entry.name}”, ${entry.source === 'admin' ? 'added by' : 'extracted by'}`}>
        <SourceTags source={entry.source} />
    </TagRow>
);

const InfoTitle: FC<{ info: string; children: ReactNode }> = ({ info, children }) => (
    <div className="flex items-center gap-1.5">
        <CardTitle>{children}</CardTitle>
        <Tooltip>
            <TooltipTrigger asChild>
                <button type="button" className="text-muted-foreground hover:text-foreground" aria-label={info}>
                    <Info className="h-3.5 w-3.5" />
                </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{info}</TooltipContent>
        </Tooltip>
    </div>
);

const CompactCard: FC<{ title: string; tier: MatchTier; badge?: ReactNode; children: ReactNode }> = ({
    title,
    tier,
    badge,
    children,
}) => (
    <TierCard title={title} tier={tier} hint={<RecallTierHint tier={tier} />} badge={badge}>
        {children}
    </TierCard>
);

const RecallTierBadge: FC<{ tier: MatchTier }> = ({ tier }) => (
    <TierBadge tier={tier} hint={<RecallTierHint tier={tier} />} />
);

/** What each gate card decides, broadest first, in the strength terms the rule chips use. */
const RecallTierHint: FC<{ tier: MatchTier }> = ({ tier }) => {
    switch (tier) {
        case 1:
            return (
                <>
                    <span>Nothing matches unless the unit’s brand is one of these.</span>
                    <TagRow label="Gates every rule">
                        <StrengthTag strength="strong" />
                        <StrengthTag strength="weak" />
                    </TagRow>
                </>
            );
        case 2:
            return (
                <>
                    <StrengthLine strength="strong">
                        An exact model number of {STRONG_IDENTIFIER_MIN_LENGTH}+ letters and digits, with at least one
                        of each. Counts for units of these types; on a unit of another type it is possibly affected and
                        the owner is asked the type.
                    </StrengthLine>
                    <StrengthLine strength="weak">
                        Everything else counts only for units of these types — or of a type in the same family, which
                        asks the owner the type — including every-model rules and a longer model number that starts with
                        a listed exact one. A custom-typed unit is asked whether it’s the product instead.
                    </StrengthLine>
                </>
            );
        case 3:
            return (
                <>
                    <StrengthLine strength="strong">
                        An exact model number of {STRONG_IDENTIFIER_MIN_LENGTH}+ letters and digits, with at least one
                        of each, matched whole. On a unit of another type it asks the owner the type instead of dropping
                        the match.
                    </StrengthLine>
                    <StrengthLine strength="weak">
                        Everything else. Matches only units of the types in Applies To, or of a type in the same family
                        (asked).
                    </StrengthLine>
                    <span>
                        A longer model number that starts with a listed exact one is always{' '}
                        <Tag className="bg-amber-200 text-amber-900">
                            {RECALL_VERDICT_META['possibly-affected'].label}
                        </Tag>
                    </span>
                </>
            );
    }
};

/**
 * Search-to-add over the brand catalog on the same fold that built the alias keys, so a pasted
 * "Whirlpool Corporation" still finds WHIRLPOOL; each row carries the brand's curation icon.
 */
const BrandPicker: FC<{ brands: Brand[]; exclude: BrandId[]; onPick: (id: BrandId) => void }> = ({
    brands,
    exclude,
    onPick,
}) => {
    const [query, setQuery] = useState('');
    const options = useMemo(() => {
        const needle = normalizeBrandName(query).key;
        return brands
            .filter(
                (b) =>
                    !exclude.includes(b.id) &&
                    (!needle ||
                        b.canonicalKey.includes(needle) ||
                        b.aliases.some((alias) => normalizeBrandName(alias).key.includes(needle))),
            )
            .sort((a, b) => a.canonicalName.localeCompare(b.canonicalName))
            .slice(0, 30);
    }, [brands, exclude, query]);
    return (
        <Popover>
            <PopoverTrigger asChild>
                <IconAddButton label="Add a brand" />
            </PopoverTrigger>
            <PopoverContent className="w-[300px]">
                <Input
                    placeholder="Search brands…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                />
                <div className="mt-2 max-h-[280px] overflow-auto">
                    {options.map((b) => (
                        <button
                            key={b.id}
                            type="button"
                            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-accent"
                            onClick={() => onPick(b.id)}
                        >
                            <BrandReviewStatusIcon brand={b} />
                            <span className="truncate">{b.displayName ?? b.canonicalName}</span>
                        </button>
                    ))}
                    {options.length === 0 && (
                        <div className="px-2 py-1 text-sm text-muted-foreground">No brands match.</div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

/**
 * A plus that opens a small field in place: Enter adds and stays open for the next; Escape, or leaving it empty,
 * closes it. `text` labels the plus.
 */
const InlineAdd: FC<{ label: string; placeholder: string; text?: string; onAdd: (value: string) => void }> = ({
    label,
    placeholder,
    text,
    onAdd,
}) => {
    const [value, setValue] = useState<string>();
    if (value === undefined && text) return <TextAddButton text={text} title={label} onClick={() => setValue('')} />;
    if (value === undefined) return <IconAddButton label={label} onClick={() => setValue('')} />;
    const close = () => setValue(undefined);
    return (
        <form
            className="inline-flex"
            onSubmit={(event) => {
                event.preventDefault();
                const trimmed = value.trim();
                if (!trimmed) return close();
                onAdd(trimmed);
                setValue('');
            }}
        >
            <Input
                className="h-6 w-[160px] px-1.5 font-mono text-xs"
                value={value}
                placeholder={placeholder}
                aria-label={label}
                autoFocus
                autoComplete="off"
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={(event) => event.key === 'Escape' && close()}
                onBlur={() => !value.trim() && close()}
            />
        </form>
    );
};

const TextAddButton: FC<{ text: string } & ComponentProps<typeof Button>> = ({ text, ...props }) => (
    <Button variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs" {...props}>
        <Plus className="h-3.5 w-3.5" />
        {text}
    </Button>
);

const IconAddButton: FC<{ label: string } & ComponentProps<typeof Button>> = ({ label, ...props }) => (
    <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={label} title={label} {...props}>
        <Plus className="h-3.5 w-3.5" />
    </Button>
);
